import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useChainBrandsStore } from "@/stores/chainBrand";
import { useQuery } from "@tanstack/vue-query";
import _ from "lodash";
import { storeToRefs } from "pinia";
import { ref, watch } from "vue";
import { Brand } from "../../../../interfaces/Brand";

interface Props {
  immediate?: boolean;
  params?: Params;
}

interface Params {
  id: number | string;
  page?: number | string;
}

const getChainBrands = async (
  params: Params,
): Promise<PaginatedResponse<Brand>> => {
  const { data } = await axios.get<PaginatedResponse<Brand>>(
    `/api/internal/chains/${params.id}/brands?page=${params.page}`,
  );

  return data;
};

const useChainBrands = (props: Props) => {
  const { setChainBrands, setPagination } = useChainBrandsStore();
  const { chainBrands, pagination } = storeToRefs(useChainBrandsStore());
  const { immediate = true } = props;
  const page = ref(1);

  const chainId = ref<number | string>(0);

  const setChainId = (id: number | string) => {
    chainId.value = id;
  };

  const setPage = (numberPage: number) => {
    page.value = numberPage;
  };

  const { data, isLoading, refetch, isFetching } = useQuery(
    ["chain-brands", chainId.value],
    () =>
      getChainBrands({
        id: chainId.value || props.params?.id!,
        page: page.value,
      }),
    {
      enabled: immediate,
    },
  );

  watch(
    data,
    (response) => {
      if (response) {
        setChainBrands(
          response.data.map((brand) => {
            return {
              chainId: brand.chainId,
              id: brand.id,
              name: brand.name,
              chainName: brand.chainName,
              createdAt: brand.createdAt,
              updatedAt: brand.updatedAt,
              hotelsCount: brand.hotelsCount,
              isActive: brand.isActive,
              logo: brand.logo,
              checked: false,
            };
          }),
        );
        setPagination(_.omit(response, ["data"]));
      }
    },
    {
      immediate,
    },
  );

  return {
    data,
    isLoading,
    refetch,
    chainId,
    setChainId,
    isFetching,
    chainBrands,

    page,
    setPage,
  };
};

export default useChainBrands;
